import React, { useEffect, useRef, useState } from 'react';
import { Input as AntdInput } from 'antd';
import { TextAreaProps as AntdTextAreaProps } from 'antd/lib/input/TextArea';
import styled from 'styled-components';
import { useInput, UseInputParams } from 'utils/hooks/useInput';

export interface TextAreaProps
  extends UseInputParams<string, HTMLTextAreaElement>,
    Omit<AntdTextAreaProps, 'onChange' | 'onPressEnter' | 'onBlur' | 'defaultValue' | 'onFocus'> {
  initialValue?: string;
  saveStatus?: {
    isSaving: boolean;
    acknowledgement?: {
      requestId: string;
      value: string;
      successful: boolean;
    };
  };
  onPressEnter?: (value: string) => void;
  allowEnterNextLine?: boolean;
}

const STextArea = styled(AntdInput.TextArea)`
  cursor: auto;
`;

export const TextArea = ({
  initialValue,
  saveStatus,
  onChange,
  onChangeDebounced,
  onBlur,
  onBlurDebounced,
  onPressEnter,
  allowEnterNextLine = false,
  ...props
}: TextAreaProps) => {
  const inputs = useInput<string, HTMLTextAreaElement>({
    onChange,
    onChangeDebounced,
    onBlur,
    onBlurDebounced,
  });
  const [focus, setFocus] = useState(false);
  const [value, setValue] = useState(initialValue);
  const [isDirty, setIsDirty] = useState(false);
  const [awaitingServerEcho, setAwaitingServerEcho] = useState<string>();
  const processedAcknowledgementId = useRef<string | undefined>(undefined);
  const saveAcknowledgement = saveStatus?.acknowledgement;

  useEffect(() => {
    if (
      !saveAcknowledgement ||
      saveStatus?.isSaving ||
      saveAcknowledgement.requestId === processedAcknowledgementId.current
    ) {
      return;
    }

    processedAcknowledgementId.current = saveAcknowledgement.requestId;
    if (!saveAcknowledgement.successful) {
      return;
    }

    if (saveAcknowledgement.value !== value) {
      if (awaitingServerEcho !== undefined && awaitingServerEcho !== saveAcknowledgement.value) {
        setAwaitingServerEcho(saveAcknowledgement.value);
      }
      return;
    }

    setIsDirty(false);
    setAwaitingServerEcho(initialValue === saveAcknowledgement.value ? undefined : saveAcknowledgement.value);
  }, [awaitingServerEcho, initialValue, saveAcknowledgement, saveStatus?.isSaving, value]);

  useEffect(() => {
    if (awaitingServerEcho !== undefined) {
      if (initialValue === awaitingServerEcho) {
        setAwaitingServerEcho(undefined);
        if (value === awaitingServerEcho) {
          setIsDirty(false);
        }
      }
      return;
    }

    if (initialValue === value) {
      setIsDirty(false);
      return;
    }

    if (!focus && !isDirty) {
      setValue(initialValue);
    }
  }, [awaitingServerEcho, focus, initialValue, isDirty, value]);

  return (
    <STextArea
      rows={props.size === 'large' ? 5 : 2}
      {...props}
      value={value}
      onChange={(e) => {
        setValue(e.target.value);
        setIsDirty(true);
        inputs.onChange(e.target.value, e);
      }}
      onBlur={(e) => {
        setValue(e.target.value);
        setFocus(false);
        inputs.onBlur(e.target.value, e);
      }}
      onPressEnter={(e) => {
        if (onPressEnter) onPressEnter(e.currentTarget.value);
        else !allowEnterNextLine && e.currentTarget.blur();
      }}
      onFocus={() => setFocus(true)}
    />
  );
};
